import React, {
    useEffect,
    useState,
    useRef
} from "react";
import Skeleton from '@mui/material/Skeleton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { setSubtitleLangList } from '@modules/subtitle';
import { setChapterLangList } from '@modules/chapter';
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from 'react-redux';
import api from '@utils/axiosInstance';
import videojs from "video.js";
import chaptersMore from "videojs-chapters-extension";
import { setPlayTime } from '@modules/media';
import "video.js/dist/video-js.css";
import "videojs-chapters-extension/dist/videojs-chapters-extension.css";
import "videojs-chapters-extension/src/css/fade.css";

videojs.registerPlugin('chaptersMore', chaptersMore);

const MultiLangPlayer = ({ setVideoDurationTime }) => {
    const { uuid } = useParams();
    const { t } = useTranslation();

    const dispatch = useDispatch();

    // videojs 타입 명시
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<videojs.Player | null>(null);

    const [player, setPlayer] = useState<videojs.Player | null>(null);

    const [availableSubtitleLanguages, setAvailableSubtitleLanguages] = useState([]);
    const [availableChapterLanguages, setAvailableChapterLanguages] = useState([]);

    const cdn = useSelector((state) => state.organization.cdn);
    const VSCdn = useSelector((state) => state.organization.VSCdn);
    const userToken = useSelector((state) => state.organization.userToken);
    const organizationData = useSelector((state) => state.organization.organizationData);
    const selectedPlayTime = useSelector((state) => state.media.selectedPlayTime);
    const lang = useSelector((state) => state.ui.lang); // 추가: 현재 UI 언어 코드

    useEffect(() => {
        const fetchSubtitleData = async () => {
            if (uuid && organizationData && userToken) {
                try {
                    const response = await api({
                        method: 'post',
                        url: '/subtitle/getState',
                        data: {
                            tk: userToken,
                            uuid,
                            ogc: organizationData.organization_code,
                            type: "ALL"
                        }
                    });

                    if (response.data.code === 100) {
                        const subtitleLangs = response.data.data.subtitle;
                        console.log("자막 API 성공:", subtitleLangs);
                        setAvailableSubtitleLanguages(subtitleLangs);
                        dispatch(setSubtitleLangList(subtitleLangs));
                    } else {
                        console.warn("자막 데이터가 없습니다:", response.data);
                        setAvailableSubtitleLanguages([]);
                    }
                } catch (err) {
                    console.error("자막 API 실패:", err);
                    setAvailableSubtitleLanguages([]);
                }
            }
        };

        const fetchChapterData = async () => {
            if (uuid && organizationData && userToken) {
                try {
                    const response = await api({
                        method: 'post',
                        url: '/chapter/getState',
                        data: {
                            tk: userToken,
                            uuid,
                            ogc: organizationData.organization_code
                        }
                    });

                    if (response.data.code === 100) {
                        const chapterLangs = response.data.data.chapter;
                        console.log("챕터 API 성공:", chapterLangs);
                        setAvailableChapterLanguages(chapterLangs);
                        dispatch(setChapterLangList(chapterLangs));
                    } else {
                        console.warn("챕터 데이터가 없습니다:", response.data);
                        setAvailableChapterLanguages([]);
                    }
                } catch (err) {
                    console.error("챕터 API 실패:", err);
                    setAvailableChapterLanguages([]);
                }
            }
        };

        fetchSubtitleData();
        fetchChapterData();
    }, [uuid, organizationData, userToken, dispatch]);

    useEffect(() => {
        if (!playerRef.current && uuid && VSCdn && cdn) {
            const videoElement = videoRef.current;
            if (!videoElement) return;

            const newPlayer = videojs(videoElement, {
                autoplay: false,
                playbackRates: [0.5, 1, 1.25, 1.5, 2],
                width: 720,
                height: 300,
                controls: true,
                sources: [
                    {
                        src: `${VSCdn}/hls/${uuid}/mp4/${uuid}.mp4/index.m3u8`,
                        type: 'application/x-mpegURL',
                    },
                ]
            }, () => {
                console.log("Player is ready");
            });

            newPlayer.on("error", () => {
                console.error("Video.js player encountered an error:", newPlayer.error());
            });

            playerRef.current = newPlayer;
            setPlayer(newPlayer);
        }
    }, [uuid, VSCdn, cdn]);

    useEffect(() => {
        if (player) {
            const onLoadedMetadata = () => {
                console.log("loadedmetadata fired", player.currentHeight());
                setVideoDurationTime(player.duration());

                // 자막 추가
                if (availableSubtitleLanguages.length > 0) {
                    console.log('aaaadwf', lang.slice(0, 2))
                    availableSubtitleLanguages.forEach((arr) => {
                        player.addRemoteTextTrack({
                            src: `${cdn}/${uuid}/subtitle/${arr.lang}/${uuid}-${arr.lang}.vtt`,
                            kind: "subtitles",
                            label: t('languages.' + arr.lang),
                            language: arr.lang,
                            default: arr.lang === lang.slice(0, 2), // lang과 일치하면 default
                            mode: arr.lang === lang.slice(0, 2) ? "showing" : "disabled"
                        }, true);
                    });
                }

                // 챕터 추가
                if (availableChapterLanguages.length > 0) {
                    const timestamp = Date.now();
                    // lang과 일치하는 챕터 언어 우선, 없으면 기존 0번
                    const chapterLangObj = availableChapterLanguages.find((c) => c.lang === lang.slice(0, 2)) || availableChapterLanguages[0];
                    player.chaptersMore({
                        src: `${cdn}/${uuid}/chapter/${chapterLangObj.lang}/${uuid}-${chapterLangObj.lang}.json?v=${timestamp}`
                    });
                }
            };

            player.on("loadedmetadata", onLoadedMetadata);

            player.on("timeupdate", () => {
                const currentTime = player.currentTime();
                dispatch(setPlayTime(currentTime));

                // cutList.forEach(cut => {
                //   if (currentTime >= cut.from && currentTime <= cut.to) {
                //     player.currentTime(cut.to);
                //   }
                // });


            });

            player.on('resize', () => {
                const currentHeight = player.currentHeight();
                console.log('현재 비디오 높이:', currentHeight);
                // 여기에 원하는 로직 추가
            });

            // 정리(cleanup)
            return () => {
                player.off("loadedmetadata", onLoadedMetadata);
            };
        }
    }, [player, availableSubtitleLanguages, availableChapterLanguages, lang]);

    useEffect(() => {
        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
            }
        };
    }, []);

    useEffect(() => {
        if (selectedPlayTime && playerRef.current) {
            playerRef.current.currentTime(selectedPlayTime);
        }
    }, [selectedPlayTime]);

    return (
        <div className="video-container">
            <div className="box-video">
                <div data-vjs-player style={{ width: '100%', height: '100%' }}>
                    {videoRef ? (
                        <>
                            <video
                                ref={videoRef}
                                id="videoEl"
                                className="video-js vjs-big-play-centered"
                            />
                        </>

                    ) : (
                        <div className="multi-lang-player-skeleton-container">
                            <Skeleton variant="rectangular" width="100%" height="100%" />
                            <PlayArrowIcon className="multi-lang-player-skeleton-icon" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MultiLangPlayer;